import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { HeartHandshake, Loader2, Send } from "lucide-react";

const BLANK = { kind: "resource", name: "", category: "mental_wellness", description: "", url: "", phone: "", region: "", perk: "", event_date: "" };
const STATUS_CHIP = {
    pending: "bg-yellow-soft text-yellow-dark",
    approved: "bg-stage_early/50 text-stage_early-dark",
    rejected: "bg-coral-light text-coral-dark",
};

/** Orgs recommend local resources/events/discounts → admin approval → Care For You. */
export default function OrgMarketplaceSection({ orgId }) {
    const [meta, setMeta] = useState({ categories: [] });
    const [form, setForm] = useState({ ...BLANK });
    const [mine, setMine] = useState([]);
    const [busy, setBusy] = useState(false);

    const load = () => api.get(`/org/marketplace/${orgId}/mine`).then(r => setMine(r.data.submissions || [])).catch(() => setMine([]));
    useEffect(() => {
        api.get("/community-partners/_meta").then(r => setMeta(r.data)).catch(() => { });
        load(); /* eslint-disable-next-line */
    }, [orgId]);

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
    const submit = async () => {
        if (!form.name.trim()) { toast("Give the resource a name."); return; }
        setBusy(true);
        try {
            await api.post(`/org/marketplace/${orgId}/submit`, form);
            toast.success("Submitted — it goes live in Care For You once Memory Lane approves it.");
            setForm({ ...BLANK });
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't submit.");
        } finally { setBusy(false); }
    };

    return (
        <section className="rounded-3xl border border-[#E2E1D8] bg-white p-6 md:p-8" data-testid="org-marketplace-section">
            <h2 className="text-lg font-heading font-semibold text-[#1F2624] flex items-center gap-2">
                <HeartHandshake size={18} strokeWidth={1.7} /> Recommend local resources
            </h2>
            <p className="text-sm text-[#4C5551] mt-1 max-w-2xl">
                Publish trusted local resources, events, or caregiver discounts. Memory Lane reviews each one before
                it appears in caregivers' Care For You recommendations.
            </p>

            <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                <select className="ml-input !py-2 text-sm" value={form.kind} onChange={e => set("kind", e.target.value)} data-testid="mkt-kind">
                    <option value="resource">Resource / service</option>
                    <option value="event">Event</option>
                    <option value="discount">Discount / perk</option>
                </select>
                <input className="ml-input !py-2 text-sm" placeholder="Name (e.g. YMCA Caregiver Yoga)" value={form.name} onChange={e => set("name", e.target.value)} data-testid="mkt-name" />
                <select className="ml-input !py-2 text-sm" value={form.category} onChange={e => set("category", e.target.value)} data-testid="mkt-category">
                    {(meta.categories || []).map(c => <option key={c.key || c} value={c.key || c}>{c.label || c}</option>)}
                </select>
                <input className="ml-input !py-2 text-sm" placeholder="City / region" value={form.region} onChange={e => set("region", e.target.value)} data-testid="mkt-region" />
                <input className="ml-input !py-2 text-sm" placeholder="Website (optional)" value={form.url} onChange={e => set("url", e.target.value)} data-testid="mkt-url" />
                <input className="ml-input !py-2 text-sm" placeholder="Phone (optional)" value={form.phone} onChange={e => set("phone", e.target.value)} />
                {form.kind === "event" && (
                    <input className="ml-input !py-2 text-sm" type="date" value={form.event_date} onChange={e => set("event_date", e.target.value)} data-testid="mkt-event-date" />
                )}
                {form.kind === "discount" && (
                    <input className="ml-input !py-2 text-sm" placeholder="Perk (e.g. 20% off for caregivers)" value={form.perk} onChange={e => set("perk", e.target.value)} data-testid="mkt-perk" />
                )}
            </div>
            <textarea rows={2} className="ml-input mt-2.5 text-sm !py-2" placeholder="Why caregivers will love this (1-2 sentences)"
                value={form.description} onChange={e => set("description", e.target.value)} data-testid="mkt-description" />
            <button type="button" onClick={submit} disabled={busy} data-testid="mkt-submit"
                className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-2xl text-sm font-semibold bg-teal text-white hover:bg-teal-dark disabled:opacity-60">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Submit for approval
            </button>

            {mine.length > 0 && (
                <div className="mt-5 border-t border-cream-dark pt-4" data-testid="mkt-mine">
                    <p className="text-xs font-semibold uppercase tracking-wide text-[#6E7A74]">Your submissions</p>
                    <ul className="mt-2 space-y-1.5">
                        {mine.map(s => (
                            <li key={s.id} className="flex items-center gap-2 text-sm text-[#4C5551]" data-testid={`mkt-row-${s.id}`}>
                                <span className={`px-2 py-0.5 rounded-2xl text-[11px] font-semibold capitalize ${STATUS_CHIP[s.status] || ""}`}>{s.status}</span>
                                <span className="text-[#1F2624] font-medium">{s.name}</span>
                                <span className="text-xs text-[#6E7A74]">· {s.kind}{s.reject_reason ? ` · ${s.reject_reason}` : ""}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </section>
    );
}
